import useSWR from 'swr';
import axios from 'axios';
import { useEffect, useState } from 'react';
import useFiltersState from '../../state/useFiltersState';
import useDebounce from '../../hooks/useDebounce';
import ChargerModule from '../../components/chargerModule';
import ShoppingHeader from '../../components/shoppingHeader';
import ShoppingList from '../../components/shoppingList';
import { getListOfOrdersFirstView, getListOfOrdersSecondView } from '../../services/orders';
import { IDataShopping, IProductList, ISharedModules, ORDER_PROPERTIES_TO_SEARCH } from '../../types/orders';
import errorMessage from '../../utils/errorMessage';
import { LoaderCards } from './components/LoaderCards';
import OrdersFilters from './components/OrdersFilters/OrdersFilters';
import UnsuccessfulSearch from '../../components/unsuccessfulSearch';
import { NewOrdersFilters } from './components/indext';
import SecondShoppingHeader from '../../components/SecondShoppingHeader';
import SecondShoppingList from '../../components/SecondShoppingList';
import CustomSelect from '../../components/customSelect';
import './Shopping.scss';

const Shopping = () => {
  const {
    ordersFilters,
    numberOfOrders,
    setNumberOfOrders,
    secondOrdersFilters,
    sharedModule,
    setSharedModule,
    ordersSearchbarText,
    setOrdersSearchbarText,
    secondOrdersSearchbarText,
    setSecondOrdersSearchbarText,
    propertyToFilterSearch,
    setPropertyToFilterSearch
  } = useFiltersState();

  const [sharedModules, setSharedModules] = useState<ISharedModules[]>([
    { title: 'Vista 1', key: 1, active: true },
    { title: 'Vista 2', key: 2, active: false }
  ]);

  const [orderList, setOrderList] = useState<IProductList[]>();
  const [secondOrderList, setSecondOrderList] = useState<IDataShopping>();
  const [loadingCards, setLoadingCards] = useState<boolean>(false);
  const [loadingSecondCards, setLoadingSecondCards] = useState<boolean>(false);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [makeFirstCharge, setMakeFirstCharge] = useState<boolean>(false);
  const [firstLoadSecondList, setFirstLoadSecondList] = useState<boolean>(true);
  const [makeFirstChargeSecondList, setMakeFirstChargeSecondList] = useState<boolean>(false);

  const headerSubtitle = `Compras ${typeof numberOfOrders === 'number' ? numberOfOrders : ''}`;
  const headerInfo = { title: 'Órdenes', titleRoute: '/order/main', subtitle: headerSubtitle };
  const debounceDuration = 1500;
  const isModuleFirstKey = sharedModule?.key === 1;
  const orderPropertiesToSearchValues = Object.values(ORDER_PROPERTIES_TO_SEARCH);

  const debouncedSearchText = useDebounce(ordersSearchbarText, debounceDuration);
  const debouncedSecondSearchText = useDebounce(secondOrdersSearchbarText, debounceDuration);
  const orderPropertiesToSearchKeys = Object.keys(ORDER_PROPERTIES_TO_SEARCH);

  const firstSearchObject = encodeURIComponent(
    JSON.stringify({
      name: debouncedSearchText.trim(),
      propertyToFilter: propertyToFilterSearch?.propertyToFilter
    })
  );

  const searchQueryParam = debouncedSearchText ? `search=${firstSearchObject}` : '';
  const secondSearchQueryParam = `${debouncedSecondSearchText ? `search=${debouncedSecondSearchText.trim()}` : ''}`;

  const filtersQueryParam = ordersFilters && ordersFilters.length ? `filters=${JSON.stringify(ordersFilters)}` : '';

  const secondFiltersQueryParam =
    secondOrdersFilters && secondOrdersFilters.length ? `filters=${JSON.stringify(secondOrdersFilters)}` : '';

  const debouncedFiltersQuery = useDebounce(filtersQueryParam, debounceDuration);
  const debouncedSecondFiltersQuery = useDebounce(secondFiltersQueryParam, debounceDuration);

  const queryParams =
    searchQueryParam || debouncedFiltersQuery
      ? `?${debouncedFiltersQuery}${
          debouncedFiltersQuery && searchQueryParam ? `&${searchQueryParam}` : searchQueryParam
        }`
      : '';

  const secondQueryParams =
    secondSearchQueryParam || debouncedSecondFiltersQuery
      ? `?${debouncedSecondFiltersQuery}${
          debouncedSecondFiltersQuery && secondSearchQueryParam ? `&${secondSearchQueryParam}` : secondSearchQueryParam
        }`
      : '';

  const {
    data: firstOrderListData,
    isLoading: loadingFirstList,
    error: ordersError,
    mutate: updateListOrdersWithCancellation
  } = useSWR(isModuleFirstKey && `/backoffice/trello${queryParams}`, getListOfOrdersFirstView, {
    suspense: false,
    revalidateOnFocus: false //Has been disabled because the needed revalidation scenarios are already handled by properties like:  revalidateIfStale, dedupingInterval
  });

  const {
    data: secondOrderListData,
    isLoading: loadingSecondList,
    error: secondOrderListError,
    mutate: updateSecondListOrdersWithCancellation
  } = useSWR(sharedModule?.key === 2 && `/trello/view2${secondQueryParams}`, getListOfOrdersSecondView, {
    suspense: false,
    revalidateOnFocus: false //Has been disabled because the needed revalidation scenarios are already handled by properties like:  revalidateIfStale, dedupingInterval
  });

  const secondSectionLoaders = loadingSecondCards || loadingSecondList;

  const handleSetPropertyToFilterSearch = (value: ORDER_PROPERTIES_TO_SEARCH) =>
    setPropertyToFilterSearch({
      name: value,
      propertyToFilter: orderPropertiesToSearchKeys?.find(
        key => ORDER_PROPERTIES_TO_SEARCH[key as keyof typeof ORDER_PROPERTIES_TO_SEARCH] === value
      )
    });

  const getSearchbarPlaceholder = () => {
    switch (propertyToFilterSearch?.name) {
      case ORDER_PROPERTIES_TO_SEARCH.ORGANIZATION_NAME:
        return 'Escribe el nombre de empresa';
      case ORDER_PROPERTIES_TO_SEARCH.SERIAL_NUMBER:
        return 'Escribe el número del serial';
      case ORDER_PROPERTIES_TO_SEARCH.EMPLOYEE_NAME:
        return 'Escribe el nombre del empleado';
      case ORDER_PROPERTIES_TO_SEARCH.PURCHASE_ORDER:
        return 'Escribe el número de la orden de compra (PO)';
      default:
        return 'Escribe el número de orden';
    }
  };

  const changeStateFirsLoad = (state: boolean) => setFirstLoad(state);
  const changeStateFirsLoadSecondList = (state: boolean) => setFirstLoadSecondList(state);

  const formatOrders = (ordersList: IProductList[]) => {
    const newOrders = [];
    for (const order of ordersList) {
      order.id = JSON.stringify(order.orderId);
      newOrders.push(order);
    }
    return newOrders;
  };

  const formatNewOrders = (orders: IProductList[]) => {
    const newOrders = [];
    for (const order of orders) {
      order.id = JSON.stringify(order.orderId);
      order.numberOfShipments = order?.shimpentStatus?.length || 0;
      newOrders.push(order);
    }
    return newOrders;
  };

  const sharedModulesAction = (key: number) => {
    const newList: ISharedModules[] = [];
    for (const module of sharedModules) {
      const newModule = {
        key: module.key,
        title: module.title,
        active: module.key === key ? true : false
      };
      if (newModule.active) setSharedModule(newModule);
      newList.push(newModule);
    }
    setSharedModules([...newList]);
  };

  const refreshData = async () => {
    setLoadingCards(true);
    setLoadingSecondCards(true);
    try {
      await updateListOrdersWithCancellation();
      await updateSecondListOrdersWithCancellation();
    } catch (error) {
      errorMessage();
    } finally {
      setLoadingCards(false);
      setLoadingSecondCards(false);
    }
  };

  const initalLoader = () => {
    if (!loadingFirstList || firstOrderListData || ordersError) {
      if (firstOrderListData) {
        const finalOrders = formatOrders(firstOrderListData);
        finalOrders && setNumberOfOrders(finalOrders.length);
        setOrderList(finalOrders);
      }
      if (ordersError) {
        if (axios.isCancel(ordersError)) {
          console.error('estado:request:', ordersError?.message);
        } else {
          errorMessage();
        }
      }
      if (!makeFirstCharge) {
        setMakeFirstCharge(true);
        changeStateFirsLoad(false);
      }
    }
  };

  const initialLoaderSecondView = () => {
    if (!loadingSecondList || secondOrderListData || secondOrderListError) {
      if (secondOrderListData) {
        const formatOrderList: IDataShopping = {
          unDate: {
            quantity: secondOrderListData?.unDate?.quantity || 0,
            items: formatNewOrders(secondOrderListData?.unDate?.items || [])
          },
          withDate: {
            quantity: secondOrderListData?.withDate?.quantity || 0,
            items: formatNewOrders(secondOrderListData?.withDate?.items || [])
          },
          lateDate: {
            quantity: secondOrderListData?.lateDate?.quantity || 0,
            items: formatNewOrders(secondOrderListData?.lateDate?.items || [])
          }
        };
        setSecondOrderList(formatOrderList);
      }
      if (secondOrderListError) {
        if (axios.isCancel(secondOrderListError)) {
          console.error('estado:request:', secondOrderListError?.message);
        } else {
          errorMessage();
        }
      }
      if (!makeFirstChargeSecondList) {
        setMakeFirstChargeSecondList(true);
        changeStateFirsLoadSecondList(false);
      }
    }
  };

  useEffect(() => {
    initalLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFirstList, firstOrderListData, ordersError]);

  useEffect(() => {
    initialLoaderSecondView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSecondList, secondOrderListData, secondOrderListError]);

  return (
    <div id="ordersModule">
      <ChargerModule
        infoSideBar={{ position: 'order' }}
        headerInfo={headerInfo}
        viewSearchBar
        searchBarDropdownList={
          isModuleFirstKey ? (
            <CustomSelect
              dropdownLabel="Tipo de búsqueda"
              items={orderPropertiesToSearchValues}
              value={propertyToFilterSearch?.name}
              onChangeSelect={value => handleSetPropertyToFilterSearch(value as ORDER_PROPERTIES_TO_SEARCH)}
            />
          ) : undefined
        }
        searchText={isModuleFirstKey ? ordersSearchbarText : secondOrdersSearchbarText}
        setSearchText={isModuleFirstKey ? setOrdersSearchbarText : setSecondOrdersSearchbarText}
        searchbarPlaceholder={getSearchbarPlaceholder()}
        sharedModules={sharedModules}
        sharedModule={sharedModule}
        sharedModulesAction={sharedModulesAction}
        loadingSearchBar={isModuleFirstKey ? firstLoad : firstLoadSecondList}
      >
        {isModuleFirstKey && (
          <div id="ordersListing">
            <OrdersFilters orderList={orderList} />
            <div className="cardsContainer">
              <ShoppingHeader ordersList={orderList} />
              {(loadingFirstList || loadingCards) && <LoaderCards />}
              {orderList && !!orderList.length && !loadingCards && !loadingFirstList && (
                <ShoppingList ordersList={orderList} refreshData={refreshData} />
              )}
              {!orderList?.length && !loadingCards && !loadingFirstList && (
                <UnsuccessfulSearch
                  customText={`No hay resultados para tu búsqueda, revisa el ${propertyToFilterSearch?.name?.toLocaleLowerCase()} o prueba con otro tipo de búsqueda`}
                />
              )}
            </div>
          </div>
        )}
        {!isModuleFirstKey && (
          <div id="newOrdersListing">
            <NewOrdersFilters />
            <div className="cardsContainer">
              <SecondShoppingHeader headerData={secondOrderList} />
              {secondSectionLoaders && <LoaderCards noCancel />}

              {secondOrderList &&
                !secondSectionLoaders &&
                (!!secondOrderList?.lateDate?.items?.length ||
                  !!secondOrderList?.unDate?.items?.length ||
                  !!secondOrderList?.withDate?.items?.length) && (
                  <SecondShoppingList listingData={secondOrderList} refreshData={refreshData} />
                )}

              {((!loadingSecondCards &&
                !!secondOrderList &&
                !secondOrderList?.lateDate?.items?.length &&
                !secondOrderList?.unDate?.items?.length &&
                !secondOrderList?.withDate?.items?.length) ||
                (!secondOrderList && !loadingSecondCards)) && <UnsuccessfulSearch />}
            </div>
          </div>
        )}
      </ChargerModule>
    </div>
  );
};

export default Shopping;
